import { useState } from 'react';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { LuEye, LuEyeClosed } from "react-icons/lu";
import EllipsisLoader from '../../base_component/EllipsisLoader/EllipsisLoader';
import { resetPasswordAPI } from '../../service/ApiServices';
import toast from 'react-hot-toast';

export default function Security() {
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const validationSchema = Yup.object({
        currentPassword: Yup.string()
            .required('Current password is required'),
        newPassword: Yup.string()
            .min(8, 'Password must be at least 8 characters')
            .matches(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/, 'Password must contain at least one uppercase letter, one lowercase letter, and one number')
            .required('New password is required'),
        confirmPassword: Yup.string()
            .oneOf([Yup.ref('newPassword'), null], 'Passwords must match')
            .required('Please confirm your new password')
    });

    const initialValues = {
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    };

    const areAllValuesFilled = (values) => {
        return values.currentPassword &&
            values.newPassword &&
            values.confirmPassword &&
            values.newPassword === values.confirmPassword
    }

    const handleSubmit = (values, { setSubmitting, resetForm }) => {
        const requestBody = {
            old_password: values.currentPassword,
            new_password: values.confirmPassword
        };

        resetPasswordAPI(requestBody)
            .then((response) => {
                if (response?.data?.status === 1) {
                    toast.success(response?.data?.message || 'Password updated successfully');
                    resetForm();
                } else {
                    toast.error(response?.data?.message || 'Failed to update password');
                }
            })
            .catch((error) => {
                const errorMessage = error?.response?.data?.message || 'Failed to update password';
                toast.error(errorMessage);
            })
            .finally(() => {
                setSubmitting(false);
            });
    };

    const togglePasswordVisibility = (field) => {
        switch (field) {
            case 'current':
                setShowCurrentPassword(!showCurrentPassword);
                break;
            case 'new':
                setShowNewPassword(!showNewPassword);
                break;
            case 'confirm':
                setShowConfirmPassword(!showConfirmPassword);
                break;
            default:
                break;
        }
    };

    return (
        <div className="bg-white w-full">
            <div className="max-w-4xl">
                <Formik
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                >
                    {({ values, errors, touched, isSubmitting, handleChange, handleBlur, submitCount }) => {
                        const allFilled = areAllValuesFilled(values);

                        return (
                            <Form className="space-y-6">
                                <div className="grid grid-cols-12 text-sm gap-4 items-center">
                                    <label
                                        htmlFor="currentPassword"
                                        className="col-span-12 sm:col-span-4 text-sm font-medium text-[#667085] flex items-center gap-2"
                                    >
                                        Current Password
                                    </label>

                                    <div className="col-span-12 sm:col-span-8">
                                        <div className="relative">
                                            <Field
                                                type={showCurrentPassword ? 'text' : 'password'}
                                                id="currentPassword"
                                                name="currentPassword"
                                                value={values.currentPassword}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                placeholder="Enter current password"
                                                className={`mt-1 block pe-10 text-sm w-full px-4 py-3 border placeholder:text-sm rounded-xl shadow-sm focus:border focus:outline-none focus:border-[#24B565] ${errors.currentPassword && (touched.currentPassword || submitCount > 0)
                                                    ? 'border-red-500'
                                                    : 'border-gray-300'
                                                    }`}
                                            />
                                            <button
                                                type="button"
                                                onClick={() => togglePasswordVisibility('current')}
                                                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                                            >
                                                {!showCurrentPassword ? (
                                                    <LuEyeClosed className="w-5 h-5" />
                                                ) : (
                                                    <LuEye className="w-5 h-5" />
                                                )}
                                            </button>
                                        </div>
                                        {errors.currentPassword && (touched.currentPassword || submitCount > 0) && (
                                            <p className="text-red-500 text-xs mt-1">{errors.currentPassword}</p>
                                        )}
                                    </div>
                                </div>

                                <div className="grid grid-cols-12 gap-4 items-center">
                                    <label
                                        htmlFor="newPassword"
                                        className="col-span-12 sm:col-span-4 text-sm font-medium text-[#667085] flex items-center gap-2"
                                    >
                                        New Password
                                    </label>
                                    <div className="col-span-12 sm:col-span-8">
                                        <div className="relative">
                                            <Field
                                                type={showNewPassword ? 'text' : 'password'}
                                                id="newPassword"
                                                name="newPassword"
                                                value={values.newPassword}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                placeholder="Enter new password"
                                                className={`mt-1 block pe-10 text-sm w-full px-4 py-3 border placeholder:text-sm rounded-xl shadow-sm focus:border focus:outline-none focus:border-[#24B565] ${errors.newPassword && (touched.newPassword || submitCount > 0)
                                                    ? 'border-red-500'
                                                    : 'border-gray-300'
                                                    }`}
                                            />
                                            <button
                                                type="button"
                                                onClick={() => togglePasswordVisibility('new')}
                                                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                                            >
                                                {!showNewPassword ? (
                                                    <LuEyeClosed className="w-5 h-5" />
                                                ) : (
                                                    <LuEye className="w-5 h-5" />
                                                )}
                                            </button>
                                        </div>
                                        {errors.newPassword && (touched.newPassword || submitCount > 0) && (
                                            <p className="text-red-500 text-xs mt-1">{errors.newPassword}</p>
                                        )}
                                    </div>
                                </div>

                                <div className="grid grid-cols-12 gap-4 items-center">
                                    <label
                                        htmlFor="confirmPassword"
                                        className="col-span-12 sm:col-span-4 text-sm font-medium text-[#667085] flex items-center gap-2"
                                    >
                                        Confirm New Password
                                    </label>
                                    <div className="col-span-12 sm:col-span-8">
                                        <div className="relative">
                                            <Field
                                                type={showConfirmPassword ? 'text' : 'password'}
                                                id="confirmPassword"
                                                name="confirmPassword"
                                                value={values.confirmPassword}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                placeholder="Confirm your new password"
                                                className={`mt-1 block pe-10 text-sm w-full px-4 py-3 border placeholder:text-sm rounded-xl shadow-sm focus:border focus:outline-none focus:border-[#24B565] ${errors.confirmPassword && (touched.confirmPassword || submitCount > 0)
                                                    ? 'border-red-500'
                                                    : 'border-gray-300'
                                                    }`}
                                            />
                                            <button
                                                type="button"
                                                onClick={() => togglePasswordVisibility('confirm')}
                                                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                                            >
                                                {!showConfirmPassword ? (
                                                    <LuEyeClosed className="w-5 h-5" />
                                                ) : (
                                                    <LuEye className="w-5 h-5" />
                                                )}
                                            </button>
                                        </div>
                                        {errors.confirmPassword && (touched.confirmPassword || submitCount > 0) && (
                                            <p className="text-red-500 text-xs mt-1">{errors.confirmPassword}</p>
                                        )}
                                    </div>
                                </div>

                                <div className="flex justify-end">
                                    <button
                                        type="submit"
                                        disabled={isSubmitting || !allFilled}
                                        className={`px-8 sm:w-[200px] w-full h-[52px] rounded-xl font-medium transition-colors ${isSubmitting
                                            ? 'bg-gray-400 cursor-not-allowed text-white'
                                            : allFilled
                                                ? 'bg-[#212121] text-white hover:bg-[#1a1a1a]'
                                                : 'bg-[#F0F1F3] text-black cursor-not-allowed'
                                            }`}
                                    >
                                        {isSubmitting ? (
                                            <EllipsisLoader color="white" />
                                        ) : (
                                            'Save'
                                        )}
                                    </button>
                                </div>
                            </Form>
                        )
                    }}
                </Formik>
            </div>
        </div>
    );
}

